import {
  ExportInvoicesModel,
  IStockReceived,
  IThirdPartyInvoice,
} from '$models'
import { singletonQueryClient } from '$utils'
import { coreApi, stockApi } from '$api'
import { Dayjs } from 'dayjs'
import { API_DATE_FORMAT, InvoiceDateProperty } from '$constants'

enum QueryKeys {
  getStockReceivedItems = 'stock-received-items',
}

/**
 * Get Stock Received Items
 * @param from - The start date for filtering stock received items
 * @param to - The end date for filtering stock received items
 * @param property - The property to search between (e.g. Received, Created, Invoiced)
 * @param supplierId - The supplier id to filter by (optional)
 * @param itemId - Only return invoices/purchase orders that contain this item. Optional
 * @param If there is no stock received inside the given time period, retrieve the most recent stock
 * received (before the given period). (Currently implemented to only work if the @param itemId is
 * set and if <see cref="property"/> is "Received")
 * @returns a list of StockReceived items
 */
export const getStockReceivedItems = async (
  from: Dayjs,
  to: Dayjs,
  property: InvoiceDateProperty,
  supplierId?: string,
  includeAdjustingInvoices?: boolean,
  itemId: string | undefined = undefined,
  ifNoneGetLastReceived: boolean = false
): Promise<IStockReceived[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getStockReceivedItems,
      {
        from,
        to,
        property,
        supplierId,
        includeAdjustingInvoices,
        itemId,
        ifNoneGetLastReceived,
      },
    ],
    queryFn: async ({ signal }) => {
      let formattedfrom = from.format()
      let formattedTo = to.format()

      if (property === InvoiceDateProperty.Invoiced) {
        formattedfrom = from.format(API_DATE_FORMAT)
        formattedTo = to.format(API_DATE_FORMAT)
      }

      return await stockApi.getStockReceivedItems(
        formattedfrom,
        formattedTo,
        property,
        supplierId,
        includeAdjustingInvoices,
        itemId,
        ifNoneGetLastReceived,
        signal
      )
    },
  })
}

export const getStockReceivedItemsByReferenceDate = (
  referenceDate: Dayjs,
  supplierId: string | undefined,
  maxResults: number | undefined,
  before: boolean | undefined,
  itemId?: string | undefined
): Promise<IStockReceived[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getStockReceivedItems,
      { referenceDate, supplierId, maxResults, before, itemId },
    ],
    queryFn: async ({ signal }) => {
      return await stockApi.getStockReceivedItemsByReferenceDate(
        referenceDate.format(API_DATE_FORMAT),
        supplierId,
        itemId,
        maxResults,
        before,
        signal
      )
    },
  })
}

/**
 * Export Invoices/Purchase Orders to Accounting System
 * @param combine - wether to combine them or not
 * @param invoicesIds - array of invoices ids to export
 * @param purchaseOrdersIds - array of purchase orders ids to export
 */
export const exportToAccountingSystem = async (
  combine: boolean,
  invoicesIds: string[],
  purchaseOrdersIds: string[]
): Promise<IThirdPartyInvoice[]> => {
  return await coreApi.exportInvoices(
    new ExportInvoicesModel({
      combineBySupplier: combine,
      invoiceIds: invoicesIds,
      purchaseOrderIds: purchaseOrdersIds,
    })
  )
}
